<template>
  <div class="academy-setup-wrapper mx-auto my-15 flex flex-col" :class="{ 'px-4': $vuetify.breakpoint.smAndDown }">
    <v-alert v-if="isTrial" type="info" border="left" text dense class="mb-0">{{ trialEndsMessage }}</v-alert>
    <div class="academy-setup-header flex flex-col" style="gap:1rem">
      <div class="d-flex justify-center">
        <v-chip class="getting-started-chip">
          <IconChecklist :color="chipAccentColor" size="20px" class="getting-started-chip__icon" />
          <span class="getting-started-chip__label">{{ $t("message.onboard_getting_started") }}</span>
        </v-chip>
      </div>
      <div>
        <div class="plusTextPrimary--text text-center" style="font-size: 2.125rem;">
          {{ $t("message.onboard_lets_setup_your_academy") }}</div>
        <div class="text-center plusTextSecondary--text" style="font-size: 0.875rem">
          {{ $t("message.onboard_setup_academy_explained") }}</div>
      </div>
    </div>
    <div class="academy-setup-content flex flex-col">
      <div class="flex flex-col align-center align-content-stretch" style="gap:0.5rem">
        <div class="plusTextSecondary--text" style="font-size:0.875rem">{{ $t("message.onboard_steps_completed", {
          completed: stepsCompleted, total: Object.keys(steps).length
        }) }}</div>
        <div class="flex" style="width:100%; gap:0.25rem;">
          <div v-for="n in Object.keys(steps).length" :key="n" class="rounded-xl flex-grow-1 progress-step"
            :class="{ 'progress-step--filled': n <= stepsCompleted }"
            style="height:0.5rem">
          </div>
        </div>
      </div>
      <div class="flex flex-col step-card-wrapper">
        <OnboardStep icon="mdi-bank"
          :title="shouldShowStepDetails('COMPLETE_ACADEMY_PROFILE') ? $t('message.onboard_complete_profile') : undefined"
          :description="shouldShowStepDetails('COMPLETE_ACADEMY_PROFILE') ? $t('message.onboard_complete_profile_explanation') : $t('message.onboard_complete_profile')"
          :suggested="isPrimaryStep('COMPLETE_ACADEMY_PROFILE')"
          :show-suggested-label="isSuggestedStep('COMPLETE_ACADEMY_PROFILE')"
          :completed="steps.COMPLETE_ACADEMY_PROFILE" @mouseenter.native="onSetupStepEnter('COMPLETE_ACADEMY_PROFILE')"
          @mouseleave.native="onSetupStepLeave" @click="goToAcademySettingsWithTour" />
        <OnboardStep icon="mdi-account-multiple-plus-outline"
          :title="shouldShowStepDetails('ADD_YOUR_MEMBERS') ? $t('message.onboard_add_your_members') : undefined"
          :description="shouldShowStepDetails('ADD_YOUR_MEMBERS') ? $t('message.onboard_add_your_members_explanation') : $t('message.onboard_add_your_members')"
          :suggested="isPrimaryStep('ADD_YOUR_MEMBERS')" :show-suggested-label="isSuggestedStep('ADD_YOUR_MEMBERS')"
          :completed="steps.ADD_YOUR_MEMBERS" @mouseenter.native="onSetupStepEnter('ADD_YOUR_MEMBERS')"
          @mouseleave.native="onSetupStepLeave" @click="goToMembersAdminWithTour" />
        <OnboardStep icon="mdi-calendar-month-outline"
          :title="shouldShowStepDetails('SETUP_CLASSES_SCHEDULE') ? $t('message.onboard_setup_classes_schedule') : undefined"
          :description="shouldShowStepDetails('SETUP_CLASSES_SCHEDULE') ? $t('message.onboard_setup_classes_schedule_explanation') : $t('message.onboard_setup_classes_schedule')"
          :suggested="isPrimaryStep('SETUP_CLASSES_SCHEDULE')"
          :show-suggested-label="isSuggestedStep('SETUP_CLASSES_SCHEDULE')" :completed="steps.SETUP_CLASSES_SCHEDULE"
          @mouseenter.native="onSetupStepEnter('SETUP_CLASSES_SCHEDULE')" @mouseleave.native="onSetupStepLeave"
          @click="goToCalendarAdminWithTour" />
        <OnboardStep icon="mdi-tag-outline"
          :title="shouldShowStepDetails('SETUP_PRODUCTS_MEMBERSHIPS') ? $t('message.onboard_setup_products_memberships') : undefined"
          :description="shouldShowStepDetails('SETUP_PRODUCTS_MEMBERSHIPS') ? $t('message.onboard_setup_products_memberships_explanation') : $t('message.onboard_setup_products_memberships')"
          :suggested="isPrimaryStep('SETUP_PRODUCTS_MEMBERSHIPS')"
          :show-suggested-label="isSuggestedStep('SETUP_PRODUCTS_MEMBERSHIPS')"
          :completed="steps.SETUP_PRODUCTS_MEMBERSHIPS"
          @mouseenter.native="onSetupStepEnter('SETUP_PRODUCTS_MEMBERSHIPS')" @mouseleave.native="onSetupStepLeave"
          @click="goToProductsMembershipsWithTour" />
        <OnboardStep v-if="paymentsReady" :icon="paymentStepIcon"
          :title="shouldShowStepDetails('CONNECT_PAYMENTS') ? $t('message.onboarding_transactions_title') : undefined"
          :description="shouldShowStepDetails('CONNECT_PAYMENTS') ? $t('message.onboard_connect_payments_explanation') : $t('message.onboarding_transactions_title')"
          :suggested="isPrimaryStep('CONNECT_PAYMENTS')" :show-suggested-label="isSuggestedStep('CONNECT_PAYMENTS')"
          :completed="steps.CONNECT_PAYMENTS" @mouseenter.native="onSetupStepEnter('CONNECT_PAYMENTS')"
          @mouseleave.native="onSetupStepLeave" @click="goToTransactionsAdminWithTour" />

      </div>
      <div class="flex flex-col" style="gap:0.5rem">
        <div class="plusTextSecondary--text">{{ $t("message.onboard_preview_full_setup") }}</div>
        <div>
          <OnboardStep icon="mdi-play-speed" iconColor="plusTealLighten1" show-chevron
            :title="$t('message.onboard_complete_tour')"
            :description="$t('message.onboard_complete_tour_explanation')" @click="handleStartOnboarding" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import OnboardStep from "@/components/displays/OnboardStep";
import IconChecklist from "@/components/displays/icons/IconChecklist.vue";
import IconPriceChange from "@/components/displays/icons/IconPriceChange.vue";
import Api2 from "@/services/Api2";
import { completeOnboardingStep } from "@/services/onboardingSteps";

export default {
  components: {
    OnboardStep,
    IconChecklist
  },
  data() {
    return {
      paymentStepIcon: IconPriceChange,
      suggested: "COMPLETE_ACADEMY_PROFILE",
      // Defaults true until fetchStepStatus() resolves - false means this
      // academy has no available payments module at all (e.g. a country
      // Stripe doesn't support), so "connect payments" isn't a real step
      // for them and is excluded from `steps` entirely, not just hidden.
      paymentsReady: true,
      /** @type {string | null} */
      hoveredSetupStep: null,
      /** @type {number | null} */
      hoverClearTimeoutId: null,
      /** @type {Record<string, boolean>} */
      steps: {
        COMPLETE_ACADEMY_PROFILE: false,
        ADD_YOUR_MEMBERS: false,
        SETUP_PRODUCTS_MEMBERSHIPS: false,
        CONNECT_PAYMENTS: false,
        SETUP_CLASSES_SCHEDULE: false
      }
    }
  },
  computed: {
    stepsCompleted: function () {
      return Object.keys(this.steps).reduce((acc, v) => {
        if (this.steps[v]) {
          acc += 1;
        }
        return acc;
      }, 0);
    },
    chipAccentColor() {
      return this.$vuetify.theme.dark ? 'plusIndigoLighten4' : 'plusIndigoAccent2';
    },
    isTrial() {
      return !!(this.$store.state.academyAdminStatus && this.$store.state.academyAdminStatus.isTrial);
    },
    trialDaysRemaining() {
      const raw = this.$store.state.academyAdminStatus?.subscriptionEndsAt;
      if (!raw) return null;
      const end = new Date(raw);
      if (Number.isNaN(end.getTime())) return null;
      const now = new Date();
      const startToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
      );
      const endDay = new Date(
        end.getFullYear(),
        end.getMonth(),
        end.getDate(),
      );
      return Math.round(
        (endDay.getTime() - startToday.getTime()) / (1000 * 60 * 60 * 24),
      )
    },
    trialEndsMessage() {
      const days = this.trialDaysRemaining;
      if (days === null) {
        return this.$t("message.admin_plus_trial_ends_fallback");
      }
      if (days < 0) {
        return this.$t("message.admin_plus_trial_expired");
      }
      if (days === 0) {
        return this.$t("message.admin_plus_trial_ends_today");
      }
      if (days === 1) {
        return this.$t("message.admin_plus_trial_ends_one_day");
      }
      return this.$t("message.admin_plus_trial_ends_in_days", { days });
    }
  },
  beforeDestroy() {
    this.clearHoverClearTimeout();
  },
  mounted() {
    this.fetchStepStatus();
  },
  methods: {
    clearHoverClearTimeout() {
      if (this.hoverClearTimeoutId != null) {
        clearTimeout(this.hoverClearTimeoutId);
        this.hoverClearTimeoutId = null;
      }
    },
    /** @param {string} stepKey */
    onSetupStepEnter(stepKey) {
      if (this.steps[stepKey]) {
        return;
      }
      this.clearHoverClearTimeout();
      this.hoveredSetupStep = stepKey;
    },
    onSetupStepLeave() {
      this.clearHoverClearTimeout();
      this.hoverClearTimeoutId = window.setTimeout(() => {
        this.hoverClearTimeoutId = null;
        this.hoveredSetupStep = null;
      }, 1000);
    },
    /** @param {string} stepKey */
    isPrimaryStep(stepKey) {
      if (this.hoveredSetupStep != null) {
        return this.hoveredSetupStep === stepKey;
      }
      return this.suggested === stepKey;
    },
    /** @param {string} stepKey */
    isSuggestedStep(stepKey) {
      return this.suggested === stepKey;
    },
    /** @param {string} stepKey */
    shouldShowStepDetails(stepKey) {
      return this.isPrimaryStep(stepKey) || this.steps[stepKey];
    },
    handleStartOnboarding() {
      this.$emit("start-onboarding");
    },
    goToAcademySettingsWithTour() {
      this.$router.push({
        name: "academysettings",
        query: { tour: "true" }
      }).catch(() => { });
    },
    goToMembersAdminWithTour() {
      this.$router.push({
        name: "members-adminplus",
        query: { tour: "true" }
      }).catch(() => { });
    },
    goToProductsMembershipsWithTour() {
      this.$router.push({
        name: "productsmemberships",
        query: { tour: "true" }
      }).catch(() => { });
    },
    goToTransactionsAdminWithTour() {
      // "Connect payments" isn't mandatory - just visiting the page once is
      // enough to check it off, regardless of whether they actually finish
      // connecting Stripe. completeOnboardingStep() is a no-op if it's
      // already complete, so this is safe to fire on every click.
      if (this.paymentsReady) {
        completeOnboardingStep({
          step: "automaticPayments",
          managedAcademy: this.$store.state.managedAcademy,
          store: this.$store,
        });
      }
      this.$router.push({
        name: "transactions-adminplus",
        query: { tour: "true" }
      }).catch(() => { });
    },
    goToCalendarAdminWithTour() {
      this.$router.push({
        name: "calendar-adminplus",
        query: { tour: "true" }
      }).catch(() => { });
    },
    async fetchStepStatus() {
      const response = await Api2().post('trial/stepState', { "managedAcademy": this.$store.state.managedAcademy });
      this.$store.dispatch("setOnboardingStepState", response.data);

      this.paymentsReady = response.data?.paymentsReady !== false;

      this.steps = {
        COMPLETE_ACADEMY_PROFILE: !!response.data?.academyProfile,
        ADD_YOUR_MEMBERS: !!response.data?.addedMembers,
        SETUP_PRODUCTS_MEMBERSHIPS: !!response.data?.setupProductsMemberships,
        SETUP_CLASSES_SCHEDULE: !!response.data?.setupSchedule
      };
      // Only a real, actionable step when payments are actually available -
      // otherwise it's excluded entirely, not just marked done, so the
      // progress count (Object.keys(steps).length) reflects the steps this
      // academy can actually complete.
      if (this.paymentsReady) {
        this.steps.CONNECT_PAYMENTS = !!response.data?.automaticPayments;
      }

      const suggestedOrder = ["COMPLETE_ACADEMY_PROFILE", "ADD_YOUR_MEMBERS", "SETUP_CLASSES_SCHEDULE",
        "SETUP_PRODUCTS_MEMBERSHIPS", "CONNECT_PAYMENTS"];
      for (const stepName of suggestedOrder) {
        if (this.steps[stepName] === false) {
          this.suggested = stepName;
          break;
        }
      }
    }
  }
}
</script>

<style scoped>
.academy-setup-wrapper {
  gap: 3rem;
  max-width: 34.438rem;
}


.academy-setup-header {
  gap: 1rem;
}

.academy-setup-content {
  gap: 2rem;
}

.step-card-wrapper {
  gap: 0.75rem;
}

.progress-step {
  background-color: rgba(92, 107, 192, 0.25);
}

.progress-step--filled {
  background-color: var(--v-plusIndigoLighten1-base);
}
</style>

<style>
.v-application .theme--light.getting-started-chip.v-chip,
.v-application .theme--light .getting-started-chip.v-chip {
  background-color: #F6F7FF !important;
  color: var(--v-plusIndigoAccent2-base) !important;
  font-weight: 600;
}

.v-application .theme--dark.getting-started-chip.v-chip,
.v-application .theme--dark .getting-started-chip.v-chip {
  background-color: var(--v-plusGreyDarken3-base) !important;
  color: var(--v-plusIndigoLighten4-base) !important;
  font-weight: 600;
}

.v-application .getting-started-chip.v-chip .v-chip__content {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.v-application .getting-started-chip__icon {
  display: block !important;
  margin: 0 !important;
  flex-shrink: 0;
}

.v-application .getting-started-chip__label {
  display: inline-block;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
